import { useQuery } from "@apollo/client";
import Box from "@mui/material/Box";
import { FC, useMemo, useRef, useState } from "react";
import { BarChartColors } from "../../../../../../components/charts/constants";
import SimpleColumnChart from "../../../../../../components/charts/SimpleColumnChart/SimpleColumnChart";
import { COMPANY_SEARCH_CLIENT } from "../../../../../../utils/consts";
import { useWorkspaceStore } from "../../../../../../store/workspace/WorkspaceStore";

import { useGetMarketSize } from "../../../../hooks/useGetMarketSize";
import {
  MARKET_PENETRATION_QUERY,
  MarketPenetrationPayload,
} from "../../../../../../apis/companySearch/marketPenetration";
import {
  determineChartContent,
  formatPenetrationPercentage,
  hasNoData,
  safeCalculatePercentage,
  isValidChartValue,
} from "../../../../utils";
import useMatchProductsById from "../../hooks/useMatchProductsById";
import { useCombinedFilters } from "../../../../../../hooks/useCombinedFilters";
import * as am4charts from "@amcharts/amcharts4/charts";
import { ChartExportMenu } from "../../../../../../components/charts/ChartExportMenu";
import { Market } from "../../../../../../types/workspace";
import { TitleWithTooltipInfo } from "../../../../../../components/charts/TitleWithTooltipInfo";
import { CHART_CONSTANTS } from "../../../constants";

interface Props {
  topProductIds: number[];
  isLoadingTopProducts: boolean;
}

const { baseId, tooltip, chartTitle, csvFieldMappings } =
  CHART_CONSTANTS.keyPlayerMarketPenetration;

export const KeyPlayerMarketPenetrationChart: FC<Props> = (props) => {
  const combinedFilters = useCombinedFilters();
  const selectedMarket = useWorkspaceStore((state) => state.selectedMarket);
  const chartRef = useRef<am4charts.XYChart>(null);
  const marketSize = useGetMarketSize();
  const market = marketSize[selectedMarket || Market.TAM];
  const localizeInstalls = useWorkspaceStore(
    (state) => state.draft?.configuration?.localizeInstalls ?? true
  );
  const workspaceWhitespace = useWorkspaceStore(
    (state) => state.draft?.configuration?.whitespace
  );
  const [isChartHovered, setIsChartHovered] = useState(false);
  const {
    matchedProductsById,
    productsNameMapById,
    loading: matchedProductsLoading,
  } = useMatchProductsById(props.topProductIds ?? []);

  const { data, loading } = useQuery<MarketPenetrationPayload>(
    MARKET_PENETRATION_QUERY,
    {
      variables: {
        companyFilters: combinedFilters,
        productIds: props.topProductIds,
        localizeInstalls,
        companyMatchFile:
          combinedFilters?.opportunityType &&
          workspaceWhitespace?.fileId &&
          workspaceWhitespace?.lastMatched
            ? workspaceWhitespace
            : undefined,
      },
      skip:
        props.topProductIds.length === 0 ||
        matchedProductsLoading ||
        market.loading,
      context: {
        clientName: COMPANY_SEARCH_CLIENT,
      },
    }
  );

  const isLoadingData = useMemo(() => {
    return (
      loading ||
      market.loading ||
      matchedProductsLoading ||
      props.isLoadingTopProducts
    );
  }, [
    matchedProductsLoading,
    loading,
    market.loading,
    props.isLoadingTopProducts,
  ]);

  const chartData = useMemo(() => {
    const sortedData = data
      ? [...data.marketPenetration]
          .filter(
            ({ companyCount }) =>
              companyCount > 0 && Number.isFinite(companyCount)
          )
          .sort((a, z) => z.companyCount - a.companyCount)
      : [];

    return (
      sortedData
        .map(({ productId, companyCount }) => {
          const product = matchedProductsById.find(
            (product) => product.id === productId
          );
          const penetrationPercentage = safeCalculatePercentage(
            companyCount,
            market.companyCount
          );
          return {
            companyCount,
            segment: productsNameMapById[productId] || "",
            percentage: penetrationPercentage,
            formattedPercentage: formatPenetrationPercentage(
              penetrationPercentage
            ),
            vendorName: product?.vendor?.name || "Unknown",
            categoryName: product?.category?.name || "Unknown",
          };
        })
        .filter(({ percentage }) => isValidChartValue(percentage)) || []
    );
  }, [data, market.companyCount, productsNameMapById, matchedProductsById]);

  const noDataReturned = useMemo(
    () => hasNoData(isLoadingData, chartData),
    [isLoadingData, chartData]
  );
  const shouldShowExport = chartRef && !noDataReturned && !isLoadingData;

  const chartContent = useMemo(() => {
    return determineChartContent({
      isLoading: isLoadingData,
      noDataReturned,
      baseId,
      chartToRender: (
        <Box height={350}>
          <SimpleColumnChart
            data={chartData}
            id={`${baseId}-chart`}
            xAxisDataPoint="segment"
            yAxisDataPoint="companyCount"
            xAxisTooltipFormat={"{segment}\n{vendorName}\n{categoryName}"}
            columnLabelFormat="{formattedPercentage}%"
            yAxisLabel="# of companies"
            barColor={BarChartColors}
            columnTooltipFormat={
              "{segment}\n[bold]{companyCount}\n{formattedPercentage}%[/]"
            }
            truncateXAxisLabels
            ref={chartRef}
          />
        </Box>
      ),
    });
  }, [chartData, isLoadingData, noDataReturned]);

  return (
    <Box
      id={`${baseId}-chart-wrapper`}
      display="flex"
      flexDirection="column"
      gap="25px"
      border="solid 1px #edeff1"
      borderRadius="10px"
      padding="20px 20px 13px"
      bgcolor={noDataReturned ? "#edeff1" : "#fff"}
      minHeight={300}
      onMouseEnter={() => setIsChartHovered(true)}
      onMouseLeave={() => setIsChartHovered(false)}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
      >
        <TitleWithTooltipInfo
          tooltipTitle={tooltip}
          chartTitle={chartTitle}
          showInfoIcon={isChartHovered}
        />
        {shouldShowExport && (
          <ChartExportMenu
            chart={chartRef}
            title={chartTitle}
            csvFieldMappings={csvFieldMappings}
          />
        )}
      </Box>
      {chartContent}
    </Box>
  );
};
